import { combineActions, handleActions } from 'redux-actions'
import { fromJS } from 'immutable'

import * as AppActions from 'actions/app'
import * as Actions from 'actions/gaio'
import * as PersonaActions from 'actions/persona_library'
import { DEFAULT_GAIO_PROMPT_FREQUENCY } from 'utils/gaio'

export const initialState = fromJS({
  showCreateView: false,
  hasProjects: null,
  selectedProjectId: null,
  projectCreateRequestRunning: false,
  projectUpdateRequestRunning: false,
  projectDeleteRequestRunning: false,
  projectSearchRequestRunning: false,
  projectsCountRequestRunning: false,
  promptsSearchRequestRunning: false,
  promptOptionsRequestRunning: false,
  promptCreateRequestRunning: false,
  promptUpdateRequestRunning: false,
  promptDeleteRequestRunning: false,
  promptsStatisticsRequestRunning: false,
  promptSearchRequestRunning: false,
  promptSearchCompleted: false,
  promptNewsRequestRunning: false,
  sourcesRequestRunning: false,
  scorecardRequestRunning: false,
  subscriptionChangeRequestRunning: false,
  promptDialogOpen: false,
  promptDialogMode: 'create',
  promptDeleteDialogOpen: false,
  promptToDelete: null,
  projectEditDialogOpen: false,
  projectDeleteDialogOpen: false,
  projectToDelete: null,
  tagSearchRequestRunning: false,
  suggestedPromptsRequestRunning: false,
  suggestionsVisible: false,
  projectSearchInputValue: '',
  promptsPage: 1,
  promptsLimit: 10,
  promptNewsPage: 1,
  promptNewsLimit: 10,
  promptsFilters: {
    personas: [],
    models: [],
    deactivated: [],
    tags: []
  },
  projectForm: {
    id: null,
    name: ''
  },
  promptForm: {
    id: null,
    projectId: null,
    title: '',
    prompt: '',
    personas: [],
    tags: [],
    models: [],
    languageId: null,
    countryId: null,
    disabled: false,
    frequency: DEFAULT_GAIO_PROMPT_FREQUENCY
  },
  originalPromptForm: null
})

export default handleActions({
  [Actions.createProjectStart]: state => state.set('projectCreateRequestRunning', true),
  [Actions.createProjectSuccess]: (state, { payload }) => state
    .set('hasProjects', true)
    .set('projectCreateRequestRunning', false)
    .set('showCreateView', false)
    .set('selectedProjectId', payload.id),
  [Actions.createProjectError]: state => state.set('projectCreateRequestRunning', false),

  [Actions.updateProjectStart]: state => state.set('projectUpdateRequestRunning', true),
  [Actions.updateProjectSuccess]: (state, { payload }) => state
    .set('projectUpdateRequestRunning', false)
    .set('projectEditDialogOpen', false)
    .set('selectedProjectId', payload.id)
    .set('projectForm', initialState.get('projectForm')),
  [Actions.updateProjectError]: state => state.set('projectUpdateRequestRunning', false),

  [Actions.deleteProjectStart]: state => state.set('projectDeleteRequestRunning', true),
  [Actions.deleteProjectSuccess]: state => state
    .set('projectDeleteRequestRunning', false)
    .set('projectDeleteDialogOpen', false)
    .set('projectToDelete', null)
    .set('activeView', 'overview')
    .set('selectedProjectId', null)
    .set('promptsFilters', initialState.get('promptsFilters'))
    .set('promptsPage', 1)
    .set('suggestionsVisible', false),
  [Actions.deleteProjectError]: state => state.set('projectDeleteRequestRunning', false),

  [Actions.createPromptStart]: state => state.set('promptCreateRequestRunning', true),
  [Actions.createPromptSuccess]: state => state
    .set('promptCreateRequestRunning', false)
    .set('promptDialogOpen', false)
    .set('promptForm', initialState.get('promptForm')),
  [Actions.createPromptError]: state => state.set('promptCreateRequestRunning', false),

  [Actions.updatePromptStart]: state => state.set('promptUpdateRequestRunning', true),
  [Actions.updatePromptSuccess]: state => state
    .set('promptUpdateRequestRunning', false)
    .set('promptDialogOpen', false)
    .set('promptForm', initialState.get('promptForm')),
  [Actions.updatePromptError]: state => state.set('promptUpdateRequestRunning', false),

  [Actions.deletePromptStart]: state => state.set('promptDeleteRequestRunning', true),
  [Actions.deletePromptSuccess]: state => state
    .set('promptDeleteRequestRunning', false)
    .set('promptDeleteDialogOpen', false)
    .set('promptToDelete', null),
  [Actions.deletePromptError]: state => state.set('promptDeleteRequestRunning', false),

  [Actions.setSelectedProject]: (state, { payload }) => state
    .set('selectedProjectId', payload.id)
    .set('promptsFilters', initialState.get('promptsFilters'))
    .set('promptsPage', 1)
    .set('suggestionsVisible', false),

  [Actions.searchProjectsStart]: state => state.set('projectSearchRequestRunning', true),
  [Actions.searchProjectsSuccess]: state => state.set('projectSearchRequestRunning', false),
  [Actions.searchProjectsError]: state => state.set('projectSearchRequestRunning', false),

  [Actions.fetchProjectsCountStart]: state => state.set('projectsCountRequestRunning', true),
  [combineActions(
    Actions.fetchProjectsCountSuccess,
    Actions.fetchProjectsCountError
  )]: state => state.set('projectsCountRequestRunning', false),

  [Actions.searchPromptsStart]: state => state
    .set('promptsSearchRequestRunning', true),
  [Actions.searchPromptsSuccess]: (state, { payload }) => {
    const nextState = state.set('promptsSearchRequestRunning', false)

    const filters = state.get('promptsFilters')
    const isUnfiltered = filters.every(values => values.isEmpty())

    if (isUnfiltered && payload.total <= 2) {
      return nextState.set('suggestionsVisible', true)
    }

    return nextState
  },
  [Actions.searchPromptsError]: state => state
    .set('promptsSearchRequestRunning', false),

  [Actions.fetchPromptsStatisticsStart]: state => state.set('promptsStatisticsRequestRunning', true),
  [combineActions(
    Actions.fetchPromptsStatisticsSuccess,
    Actions.fetchPromptsStatisticsError
  )]: state => state.set('promptsStatisticsRequestRunning', false),

  [Actions.fetchPromptOptionsStart]: state => state.set('promptOptionsRequestRunning', true),
  [combineActions(
    Actions.fetchPromptOptionsSuccess,
    Actions.fetchPromptOptionsError
  )]: state => state.set('promptOptionsRequestRunning', false),

  [Actions.searchPromptStart]: state => state
    .set('promptSearchRequestRunning', true)
    .set('promptSearchCompleted', false)
    .set('promptNewsPage', 1),
  [combineActions(
    Actions.searchPromptSuccess,
    Actions.searchPromptError
  )]: state => state
    .set('promptSearchRequestRunning', false)
    .set('promptSearchCompleted', true),

  [Actions.fetchPromptNewsStart]: state => state.set('promptNewsRequestRunning', true),
  [combineActions(
    Actions.fetchPromptNewsSuccess,
    Actions.fetchPromptNewsError
  )]: state => state.set('promptNewsRequestRunning', false),

  [Actions.sourcesRequestStart]: state => state.set('sourcesRequestRunning', true),
  [combineActions(
    Actions.sourcesRequestSuccess,
    Actions.sourcesRequestError
  )]: state => state.set('sourcesRequestRunning', false),

  [Actions.scorecardRequestStart]: state => state.set('scorecardRequestRunning', true),
  [combineActions(
    Actions.scorecardRequestSuccess,
    Actions.scorecardRequestError
  )]: state => state.set('scorecardRequestRunning', false),

  [Actions.requestSubscriptionChangeStart]: state => state.set('subscriptionChangeRequestRunning', true),
  [combineActions(
    Actions.requestSubscriptionChangeSuccess,
    Actions.requestSubscriptionChangeError
  )]: state => state.set('subscriptionChangeRequestRunning', false),

  [Actions.searchTagsStart]: state => state.set('tagSearchRequestRunning', true),
  [combineActions(
    Actions.searchTagsSuccess,
    Actions.searchTagsError
  )]: state => state.set('tagSearchRequestRunning', false),

  [Actions.fetchSuggestedPromptsStart]: state => state.set('suggestedPromptsRequestRunning', true),
  [combineActions(
    Actions.fetchSuggestedPromptsSuccess,
    Actions.fetchSuggestedPromptsError
  )]: state => state.set('suggestedPromptsRequestRunning', false),

  [Actions.setPromptsPage]: (state, { payload }) => state.set('promptsPage', payload.page),
  [Actions.setPromptsLimit]: (state, { payload }) => state
    .set('promptsLimit', payload.limit)
    .set('promptsPage', 1),
  [Actions.setPromptNewsPage]: (state, { payload }) => state.set('promptNewsPage', payload.page),
  [Actions.setPromptNewsLimit]: (state, { payload }) => state
    .set('promptNewsLimit', payload.limit)
    .set('promptNewsPage', 1),
  [Actions.setProjectSearchInputValue]: (state, { payload }) => state
    .set('projectSearchInputValue', payload.value),
  [Actions.setPromptsFilters]: (state, { payload }) => state
    .mergeIn(['promptsFilters'], fromJS(payload))
    .set('promptsPage', 1),
  [Actions.setSuggestionsVisible]: (state, { payload }) => state.set('suggestionsVisible', payload.visible),

  [Actions.openPromptCreateDialog]: (state, { payload = {} }) => state
    .set('promptDialogOpen', true)
    .set('promptDialogMode', 'create')
    .set('promptForm', initialState.get('promptForm').merge(fromJS(payload)))
    .set('originalPromptForm', null),
  [Actions.openPromptEditDialog]: (state, { payload }) => state
    .set('promptDialogOpen', true)
    .set('promptDialogMode', 'edit')
    .set('promptForm', initialState.get('promptForm').merge(fromJS(payload)))
    .set('originalPromptForm', initialState.get('promptForm').merge(fromJS(payload))),
  [Actions.closePromptFormDialog]: state => state
    .set('promptDialogOpen', false)
    .set('promptForm', initialState.get('promptForm'))
    .set('originalPromptForm', initialState.get('originalPromptForm')),
  [Actions.setPromptFormField]: (state, { payload: { key, value } }) => state.setIn(['promptForm', key], fromJS(value)),
  [Actions.openPromptDeleteDialog]: (state, { payload }) => state
    .set('promptDeleteDialogOpen', true)
    .set('promptToDelete', fromJS(payload)),
  [Actions.closePromptDeleteDialog]: state => state
    .set('promptDeleteDialogOpen', false)
    .set('promptToDelete', null),

  [PersonaActions.createPersonaSuccess]: (state, { payload }) => {
    if (!state.get('promptDialogOpen') || state.get('promptDialogMode') !== 'create' || !payload?.id) {
      return state
    }

    const personas = state.getIn(['promptForm', 'personas'])

    if (personas.includes(payload.id)) {
      return state
    }

    return state.updateIn(['promptForm', 'personas'], values => values.push(payload.id))
  },

  [Actions.openProjectEditDialog]: (state, { payload }) => state
    .set('projectEditDialogOpen', true)
    .set('projectForm', fromJS(payload)),
  [Actions.closeProjectEditDialog]: state => state
    .set('projectEditDialogOpen', false)
    .set('projectForm', initialState.get('projectForm')),
  [Actions.setProjectFormField]: (state, { payload: { key, value } }) => state.setIn(['projectForm', key], value),
  [Actions.openProjectDeleteDialog]: (state, { payload }) => state
    .set('projectDeleteDialogOpen', true)
    .set('projectToDelete', fromJS(payload)),
  [Actions.closeProjectDeleteDialog]: state => state
    .set('projectDeleteDialogOpen', false)
    .set('projectToDelete', null),

  [Actions.openCreateView]: state => state.set('showCreateView', true),
  [Actions.closeCreateView]: state => state.set('showCreateView', false),

  [Actions.setHasProjects]: (state, { payload }) => state.set('hasProjects', payload),

  [combineActions(
    Actions.resetState,
    AppActions.resetState
  )]: () => initialState
}, initialState)
